import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthService from "../services/AuthService";
import { useUser } from "../contexts/UserContext";
import BeatLoader from "react-spinners/BeatLoader";

const LoginPage = () => {
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { setUser } = useUser();

  const roleRedirectMap = {
    admin: "/xl-admin",
    superadmin: "/xl-admin",
    user: "/account",
    distributor: "/account/aff-dashboard",
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const loggedInUser = await AuthService.login({ login, password });

      setUser(loggedInUser);

      // ✅ Redirect based on role
      navigate(roleRedirectMap[loggedInUser.role] || "/account");
    } catch (error) {
      if (error.response?.status === 401) {
        setError("Login failed. Please check your credentials.");
      } else if (error.response?.status === 422) {
        setError(error.response.data.message);
      } else {
        setError("An unexpected error occurred.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getPlaceholder = () => {
    return "Email or mobile number";
  };

  const getInputType = () => {
    if (login.includes("@")) {
      return "email";
    }
    return "text";
  };

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
      <div
        className="card border-0 shadow-lg rounded-4 overflow-hidden w-100"
        style={{ maxWidth: "420px" }}
      >
        {isLoading && (
          <div className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center rounded-4 bg-white bg-opacity-90 z-3">
            <BeatLoader color="#0d6efd" loading={true} size={18} />
            <div className="mt-2 text-muted">Logging in...</div>
          </div>
        )}
        <div className="card-header bg-white text-center py-4">
          <h2 className="h4 mb-1 fw-bold text-dark">Welcome Back</h2>
          <p className="text-muted mb-0">Sign in to access your account</p>
        </div>
        <div className="card-body p-4">
          {error && (
            <div
              className="alert alert-danger d-flex align-items-center"
              role="alert"
            >
              <i className="bi bi-exclamation-circle-fill me-2"></i>
              <div>{error}</div>
            </div>
          )}
          <form onSubmit={handleLogin}>
            <div className="mb-3">
              <label className="form-label fw-semibold text-dark">
                Email or Mobile Number
              </label>
              <div className="input-group">
                <span className="input-group-text bg-light border-end-0">
                  <i className="bi bi-person text-muted"></i>
                </span>
                <input
                  type={getInputType()}
                  className="form-control border-start-0 ps-1"
                  value={login}
                  onChange={(e) => setLogin(e.target.value)}
                  placeholder={getPlaceholder()}
                  required
                />
              </div>
              <div className="form-text text-muted">
                You can login with your email address or mobile number
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label fw-semibold text-dark">
                Password
              </label>
              <div className="input-group">
                <span className="input-group-text bg-light border-end-0">
                  <i className="bi bi-lock text-muted"></i>
                </span>
                <input
                  type="password"
                  className="form-control border-start-0 ps-1"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                />
              </div>
            </div>
            <div className="mb-4 text-end">
              <button
                type="button"
                className="btn btn-link p-0 text-decoration-none text-primary"
                onClick={() => navigate("/reset")}
              >
                <i className="bi bi-question-circle me-1"></i>
                Forgot Password?
              </button>
            </div>
            <button
              type="submit"
              className="btn btn-primary w-100 py-2 mb-3 fw-semibold"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                  ></span>
                  Processing...
                </>
              ) : (
                <>
                  <i className="bi bi-box-arrow-in-right me-2"></i>
                  Login
                </>
              )}
            </button>
          </form>
          <div className="text-center mt-4 pt-3 border-top">
            <span className="me-2 text-muted">Don't have an account?</span>
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={() => navigate("/signup")}
            >
              Create Account
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
